import { usePostHog } from 'posthog-js/react'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import Shepherd from 'shepherd.js'
import type { StepOptions } from 'shepherd.js'
import type { AnalyticsEvent, AnalyticsEventName } from '@/hooks/use-analytics'
import { TourContext } from '@/hooks/use-tour'
import { markTourSeen, TOUR_REGISTRY } from '@/lib/tours/registry'
import type { TourDefinition } from '@/lib/tours/registry'
import { tourDefaultStepOptions } from '@/lib/tours/utils/default-step-options'

type PropsFor<Name extends AnalyticsEventName> = Extract<AnalyticsEvent, { name: Name }>['props']
type Capture = <Name extends AnalyticsEventName>(name: Name, props: PropsFor<Name>) => void

export function TourProvider({ children }: { children: React.ReactNode }) {
  const posthog = usePostHog()
  const activeTourRef = useRef<ReturnType<typeof buildTour> | undefined>(undefined)
  // Token for the in-flight `startTour`. Each call assigns a fresh symbol;
  // when the deferred handoff to Shepherd runs, we only proceed if the token
  // still matches. cancelActiveTour clears it, so any pending start gets
  // aborted — this prevents StrictMode's mount→unmount→remount from spinning
  // up two tours when the first call's deferred handoff races with the
  // second mount.
  const startTokenRef = useRef<symbol | undefined>(undefined)
  // SUP-3031: distinguish programmatic cancels (endTour, unmount cleanup,
  // start-while-already-active) from user-initiated cancels (X / Esc).
  // Without this, StrictMode's synthetic mount→unmount→remount fires the
  // cancel handler and permanently markSeen()s the tour after one dev
  // hot-reload.
  const isProgrammaticEndRef = useRef(false)

  const capture = useCallback<Capture>((name, props) => {
    // PostHog may be undefined before the SDK has initialized, or if it
    // failed to load. The TourProvider is mounted at the root, so an
    // uncaught throw here would unmount the entire app. Always soft-fail.
    posthog?.capture(name, props)
  }, [posthog])

  const cancelActiveTour = useCallback(() => {
    // Abort any in-flight startTour first.
    startTokenRef.current = undefined
    if (!activeTourRef.current) {
      return
    }
    isProgrammaticEndRef.current = true
    void activeTourRef.current.cancel()
    activeTourRef.current = undefined
  }, [])

  const startTour = useCallback((tourId: string) => {
    const definition = TOUR_REGISTRY[tourId]
    if (!definition) {
      return
    }
    cancelActiveTour()

    // Mint a fresh token for this start. If something cancels (cleanup, a
    // newer startTour) before the deferred handoff runs, the token won't
    // match and we'll bail.
    const token = Symbol('tour-start')
    startTokenRef.current = token

    // Microtask defer lets React commit the calling render before Shepherd
    // mutates the DOM. Anchor-wait is now per-step via
    // tourDefaultStepOptions.beforeShowPromise — each step waits only for
    // its own anchor at show time. Steps whose anchor never appears are
    // skipped via showOn; Shepherd then advances to the next showable step
    // or completes the tour.
    queueMicrotask(() => {
      if (startTokenRef.current !== token) {
        return
      }
      const tour = buildTour(definition, capture, isProgrammaticEndRef)
      activeTourRef.current = tour
      // Clear ref when the tour ends (either path) so a subsequent
      // unmount-driven endTour() no-ops instead of re-cancelling and
      // emitting a spurious tour_dismissed alongside tour_completed.
      const clearRef = () => {
        if (activeTourRef.current === tour) {
          activeTourRef.current = undefined
        }
      }
      tour.on('complete', clearRef)
      tour.on('cancel', clearRef)
      tour.start()
    })
  }, [capture, cancelActiveTour])

  const endTour = useCallback(() => {
    cancelActiveTour()
  }, [cancelActiveTour])

  useEffect(() => {
    return () => {
      cancelActiveTour()
    }
  }, [cancelActiveTour])

  const value = useMemo(() => ({ startTour, endTour }), [startTour, endTour])

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>
}

/**
 * Builds a Shepherd.Tour wired to emit PostHog funnel events on every transition.
 * Sets the seen-key on complete or user-initiated cancel so auto tours don't
 * re-fire for the same device. Programmatic cancels (signalled via
 * `isProgrammaticEndRef`) skip both analytics and markSeen — see the
 * StrictMode note in TourProvider.
 */
function buildTour(
  def: TourDefinition,
  capture: Capture,
  isProgrammaticEndRef: { current: boolean },
) {
  const totalSteps = def.steps.length
  const decoratedSteps: StepOptions[] = def.steps.map((step, index) => ({
    ...step,
    when: {
      ...step.when,
      show() {
        // After Shepherd renders the popover, inject the step counter dots
        // into the footer. Using `when.show` (not the Tour-level event) gives
        // us access to `this.el` in the Step instance.
        injectStepDots(this as unknown as { el?: HTMLElement }, index, totalSteps)
        // Add an "eyebrow" step counter inside the title for a subtle text cue.
        decorateStepTitle(this as unknown as { el?: HTMLElement }, index, totalSteps)
        // Fan out user-supplied show callbacks if any.
        const userShow = step.when && 'show' in step.when ? (step.when as { show?: () => void }).show : undefined
        if (typeof userShow === 'function') {
          userShow.call(this)
        }
      },
    },
  }))

  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: tourDefaultStepOptions,
    steps: decoratedSteps,
  })

  tour.on('start', () => {
    capture('tour_started', { tour_id: def.id })
  })

  // Track the highest step index the user has reached so `tour_step_advanced`
  // fires only on forward transitions. Clicking Back from step 3 → 2 should
  // NOT emit "advanced"; re-clicking Next 2 → 3 should not double-count an
  // already-recorded advance. Funnel semantics: count "first time reaching
  // this step", not "every show()".
  let highestStepIndex = -1
  tour.on('show', ({ step }: { step: { id?: string } }) => {
    const stepId = step.id ?? 'unknown'
    const stepIndex = def.steps.findIndex(s => s.id === stepId)
    if (stepIndex > 0 && stepIndex > highestStepIndex) {
      capture('tour_step_advanced', { tour_id: def.id, step_id: stepId, step_index: stepIndex })
    }
    if (stepIndex > highestStepIndex) {
      highestStepIndex = stepIndex
    }
  })

  tour.on('complete', () => {
    capture('tour_completed', { tour_id: def.id })
    markTourSeen(def)
  })

  tour.on('cancel', () => {
    if (isProgrammaticEndRef.current) {
      isProgrammaticEndRef.current = false
      return
    }
    const step = tour.currentStep
    const stepId = step?.id ?? 'unknown'
    const stepIndex = def.steps.findIndex(s => s.id === stepId)
    capture('tour_dismissed', { tour_id: def.id, step_id: stepId, step_index: Math.max(0, stepIndex) })
    markTourSeen(def)
  })

  return tour
}

function injectStepDots(
  ctx: { el?: HTMLElement },
  currentIndex: number,
  total: number,
) {
  if (total < 2) {
    return
  }
  const root = ctx.el
  if (!root) {
    return
  }
  const footer = root.querySelector<HTMLDivElement>('.shepherd-footer')
  if (!footer || footer.querySelector('.shepherd-step-dots')) {
    return
  }
  const dots = document.createElement('div')
  dots.className = 'shepherd-step-dots'
  dots.setAttribute('aria-label', `Step ${currentIndex + 1} of ${total}`)
  for (let i = 0; i < total; i += 1) {
    const dot = document.createElement('span')
    dot.className = `shepherd-step-dot${i === currentIndex ? ' shepherd-step-dot-active' : ''}`
    dots.append(dot)
  }
  footer.insertBefore(dots, footer.firstChild)
}

function decorateStepTitle(
  ctx: { el?: HTMLElement },
  currentIndex: number,
  total: number,
) {
  if (total < 2) {
    return
  }
  const root = ctx.el
  if (!root) {
    return
  }
  const title = root.querySelector<HTMLHeadingElement>('.shepherd-title')
  if (!title || title.querySelector('.shepherd-step-eyebrow')) {
    return
  }
  const eyebrow = document.createElement('span')
  eyebrow.className = 'shepherd-step-eyebrow'
  eyebrow.textContent = `Step ${currentIndex + 1} of ${total}`
  title.insertBefore(eyebrow, title.firstChild)
}
